import React, { forwardRef, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './KoobQuill.css';

// Every format Quill 2 registers, except `formula` and `video`: their HTML
// export does not escape the value (CVE-2025-15056, no fix in 2.0.3), and we
// store that export. Leaving them out also drops them from pasted HTML.
const SAFE_FORMATS = [
  'background',
  'bold',
  'color',
  'font',
  'code',
  'italic',
  'link',
  'size',
  'strike',
  'script',
  'underline',
  'blockquote',
  'header',
  'indent',
  'list',
  'align',
  'direction',
  'code-block',
  'image',
];
const UNSAFE_FORMATS = ['formula', 'video'];
const EMPTY = '<p></p>';

// Quill 2 keeps every list as <ol><li data-list="bullet|ordered"> inside its
// editor. PDFs, emails and the read-only screens read the stored text without
// Quill, so store its semantic HTML (plain <ul>/<ol>, as Quill 1 wrote), less
// the &nbsp; entities. react-quill-new compares the value it gets back with
// the raw semantic HTML it keeps, and reloads the editor (cursor and all) when
// they differ: hand it that raw string for the value we sent.
const KoobQuill = forwardRef<ReactQuill, ReactQuill.ReactQuillProps>(
  ({ value, onChange, formats = SAFE_FORMATS, ...props }, ref) => {
    const last = useRef<{ sent?: string; raw?: string }>({});

    return (
      <ReactQuill
        ref={ref}
        {...props}
        formats={formats.filter((format) => !UNSAFE_FORMATS.includes(format))}
        value={
          value !== undefined &&
          (value === last.current.sent ||
            // Forms store an emptied editor as '' (EditorField).
            (value === '' && last.current.raw === EMPTY))
            ? last.current.raw
            : value
        }
        onChange={(raw, delta, source, editor) => {
          // 2.0.3 writes every space as &nbsp;, which stops the text from
          // wrapping wherever it is shown. A typed non-breaking space stays a
          // character, so only Quill's entities change.
          const sent = raw.replace(/&nbsp;/g, ' ');
          last.current = { sent, raw };
          onChange?.(sent, delta, source, editor);
        }}
      />
    );
  },
);

KoobQuill.displayName = 'KoobQuill';

export default KoobQuill;
